import { Link, getRouteApi } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { PAPERS, printBlank, printSheet } from '@realmery/realm-core';
// The desktop's own print stylesheet: the PDF it exports and the page printed here are one design.
import '../../../../../src/main/resources/print/sheet-print.css';
import { sanitizeEntryHtmlToFragment } from '../../security/sanitize.ts';
import { useSettings } from '../../settings.ts';

/**
 * A character sheet, or a template's blank sheet, laid out for paper (realm-core's
 * sheetPrint.ts, the desktop's `SheetPrint`) and printed through the browser, where Save as
 * PDF keeps it as a PDF. While this page is open, printing shows the sheet alone: the app
 * bar, sidebar and these buttons are hidden (styles.css, "Printing a sheet").
 */

const sheetRoute = getRouteApi('/_app/realm/$realmKey/sheets/$sheetId/print');
const templateRoute = getRouteApi('/_app/realm/$realmKey/templates/$templateId/print');

export function SheetPrintPage() {
  const view = sheetRoute.useLoaderData();
  const { realmKey, sheetId } = sheetRoute.useParams();
  const paper = useSettings()['print.paperSize'];
  if (!view) return <Missing what="sheet" realmKey={realmKey} />;
  return (
    <PrintView
      title={view.sheet.name}
      html={printSheet(view.template, view.sheet, view.entryTitle, paper)}
      back={
        <Link to="/realm/$realmKey/sheets/$sheetId" params={{ realmKey, sheetId }}>
          ← Back to the sheet
        </Link>
      }
    />
  );
}

export function BlankSheetPrintPage() {
  const found = templateRoute.useLoaderData();
  const { realmKey } = templateRoute.useParams();
  const paper = useSettings()['print.paperSize'];
  if (!found) return <Missing what="template" realmKey={realmKey} />;
  return (
    <PrintView
      title={`${found.template.name} (blank)`}
      html={printBlank(found.template, paper)}
      back={
        <Link to="/realm/$realmKey/sheets" params={{ realmKey }}>
          ← All sheets
        </Link>
      }
    />
  );
}

function PrintView({ title, html, back }: { title: string; html: string; back: React.ReactNode }) {
  const page = useRef<HTMLDivElement>(null);
  const paper = useSettings()['print.paperSize'];

  // The page is parsed only by DOMPurify's policy (Trusted Types), as entry text is.
  useEffect(() => {
    page.current?.replaceChildren(sanitizeEntryHtmlToFragment(html));
  }, [html]);

  // Printing shows the sheet alone, and the browser suggests its name for the PDF.
  useEffect(() => {
    const root = document.documentElement;
    const before = document.title;
    root.classList.add('printing-sheet');
    document.title = title;
    return () => {
      root.classList.remove('printing-sheet');
      document.title = before;
    };
  }, [title]);

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <p className="eyebrow">{back}</p>
        <div className="actions">
          <button type="button" className="primary" onClick={() => window.print()}>
            Print or save as PDF…
          </button>
        </div>
        <p className="meta">
          To keep a PDF, choose <strong>Save as PDF</strong> as the printer. The paper is {PAPERS[paper]}, which{' '}
          <Link to="/settings">Settings</Link> can change.
        </p>
      </div>
      <div className={paper === 'LETTER' ? 'print-preview print-letter' : 'print-preview'} ref={page} aria-label="The printed sheet" role="document" />
    </div>
  );
}

function Missing({ what, realmKey }: { what: string; realmKey: string }) {
  return (
    <article className="document">
      <h1>No such {what}</h1>
      <p>
        It may have been deleted.{' '}
        <Link to="/realm/$realmKey/sheets" params={{ realmKey }}>
          All sheets
        </Link>
      </p>
    </article>
  );
}
